import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { DURATION_TIME_UNITS, STRING_UOM } from '@/main/app.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { INTERPOLATION_TYPES } from '@/tools/manualSignal/manualSignal.constants';
import { generateFormula } from '@/tools/manualSignal/manualSignal.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { InputTableSort } from '@/formbuilder/InputTableFormComponent.molecule';
import type { ManualSample } from '@/tools/manualSignal/manualSignal.constants';
import type { BaseToolPersistedState, ToolRehydratePayload } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export type ManualSignalState = BaseToolPersistedState & {
  /** Interpolation method of the output signal, one of INTERPOLATION_TYPES. */
  interpolationMethod: string;
  /** Maximum interpolation of the output signal; ignored for discrete interpolation. */
  maximumInterpolation: ValueWithUnitsItem;
  /** Unit of measure applied to the sample values, or `undefined` for unitless. */
  uom: string | undefined;
  /** Samples entered by the user. */
  samples: ManualSample[];
  /** ID of the sample being edited in the table, or `undefined` if none. */
  editingSampleId: string | undefined;
  /** Sort applied to the sample table. */
  sort: InputTableSort;
  /** Color of the output signal and preview. */
  color: string;
};

export type ManualSignalDehydratedState = Omit<ManualSignalState, 'editingSampleId'>;

type ManualSignalConfigState = Pick<
  ManualSignalState,
  | 'maximumDuration'
  | 'advancedParametersCollapsed'
  | 'configVersion'
  | 'interpolationMethod'
  | 'maximumInterpolation'
  | 'uom'
  | 'samples'
  | 'sort'
  | 'color'
>;

type ManualSignalActions = {
  /** Sets the name of the output signal. */
  setSearchName: (name: string) => void;
  /** Sets the color of the output signal and preview. */
  setColor: (color: string) => void;
  /** Sets the interpolation method of the output signal, one of INTERPOLATION_TYPES. */
  setInterpolationMethod: (interpolationMethod: string) => void;
  /** Sets the maximum interpolation of the output signal. */
  setMaximumInterpolation: (maximumInterpolation: ValueWithUnitsItem) => void;
  /** Sets the unit of measure applied to the sample values. */
  setUom: (uom: string) => void;
  /** Appends a sample, defaulting the unit of measure to string if the first sample is not numeric. */
  addSample: (sample: ManualSample) => void;
  /** Removes the sample with the given ID; does nothing if no sample has that ID. */
  removeSample: (id: string) => void;
  /** Merges the sample into the existing sample with the same ID. */
  updateSample: (sample: ManualSample) => void;
  /** Sets the sample being edited in the table, or `undefined` to stop editing. */
  setEditingSampleId: (editingSampleId: string | undefined) => void;
  /** Sets the sort applied to the sample table. */
  setSort: (sort: InputTableSort) => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: ResettableStoreState<ManualSignalState>;
};

const initialState: ManualSignalState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  interpolationMethod: INTERPOLATION_TYPES.LINEAR,
  maximumInterpolation: {
    value: 40,
    units: DURATION_TIME_UNITS[2].unit[0],
  },
  uom: undefined,
  samples: [],
  editingSampleId: undefined,
  sort: { asc: false, property: 'dateTime' },
  color: '',
});

/** Builds the UIConfig saved with the signal so the tool can be reopened for editing. */
export function getManualSignalConfigParams(
  state: ManualSignalConfigState,
): ManualSignalConfigState & { type: string } {
  return {
    type: TREND_TOOLS.MANUAL_SIGNAL,
    maximumDuration: state.maximumDuration,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
    configVersion: state.configVersion,
    interpolationMethod: state.interpolationMethod,
    maximumInterpolation: state.maximumInterpolation,
    uom: state.uom,
    samples: state.samples,
    sort: state.sort,
    color: state.color,
  };
}

export function dehydrateManualSignalState(state: ManualSignalState): ManualSignalDehydratedState {
  return {
    id: state.id,
    name: state.name,
    originalParameters: state.originalParameters,
    maximumDuration: state.maximumDuration,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
    configVersion: state.configVersion,
    interpolationMethod: state.interpolationMethod,
    maximumInterpolation: state.maximumInterpolation,
    uom: state.uom,
    samples: state.samples,
    sort: state.sort,
    color: state.color,
  };
}

const manualSignalActionsFactory: ActionsFactory<ManualSignalState, ManualSignalActions> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),
  setInterpolationMethod: (interpolationMethod) =>
    set((draft) => {
      draft.interpolationMethod = interpolationMethod;
    }),
  setMaximumInterpolation: (maximumInterpolation) =>
    set((draft) => {
      draft.maximumInterpolation = maximumInterpolation;
    }),
  setUom: (uom) =>
    set((draft) => {
      draft.uom = uom;
    }),
  addSample: (sample) =>
    set((draft) => {
      if (draft.samples.length === 0 && draft.uom === undefined && Number.isNaN(Number(sample.value))) {
        draft.uom = STRING_UOM;
      }
      draft.samples.push(sample);
    }),
  removeSample: (id) =>
    set((draft) => {
      const index = draft.samples.findIndex((sample) => sample.id === id);
      if (index > -1) {
        draft.samples.splice(index, 1);
      }
    }),
  updateSample: (sample) =>
    set((draft) => {
      const existingSample = draft.samples.find((existing) => existing.id === sample.id);
      if (existingSample) {
        Object.assign(existingSample, sample);
      }
    }),
  setEditingSampleId: (editingSampleId) =>
    set((draft) => {
      draft.editingSampleId = editingSampleId;
    }),
  setSort: (sort) =>
    set((draft) => {
      draft.sort = sort;
    }),
  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, rehydrationState.config, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),
  reset: resetState,
});

const STORE_NAME = 'ManualSignal';
const [ManualSignalStoreProvider, useManualSignalStoreInstance, useManualSignalStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  manualSignalActionsFactory,
);

export { ManualSignalStoreProvider };

const manualSignalConfigParamsSelector = createSelector(
  [
    (state: ManualSignalState) => state.maximumDuration,
    (state: ManualSignalState) => state.advancedParametersCollapsed,
    (state: ManualSignalState) => state.configVersion,
    (state: ManualSignalState) => state.interpolationMethod,
    (state: ManualSignalState) => state.maximumInterpolation,
    (state: ManualSignalState) => state.uom,
    (state: ManualSignalState) => state.samples,
    (state: ManualSignalState) => state.sort,
    (state: ManualSignalState) => state.color,
  ],
  (
    maximumDuration,
    advancedParametersCollapsed,
    configVersion,
    interpolationMethod,
    maximumInterpolation,
    uom,
    samples,
    sort,
    color,
  ) =>
    getManualSignalConfigParams({
      maximumDuration,
      advancedParametersCollapsed,
      configVersion,
      interpolationMethod,
      maximumInterpolation,
      uom,
      samples,
      sort,
      color,
    }),
);

const manualSignalFormulaSelector = createSelector(
  [
    (state: ManualSignalState) => state.interpolationMethod,
    (state: ManualSignalState) => state.maximumInterpolation,
    (state: ManualSignalState) => state.samples,
    (state: ManualSignalState) => state.uom,
  ],
  (interpolationMethod, maximumInterpolation, samples, uom) =>
    generateFormula({ interpolationMethod, maximumInterpolation, samples, uom }),
);

export const useManualSignalId = () => useManualSignalStoreSelector((state) => state.id);
export const useManualSignalName = () => useManualSignalStoreSelector((state) => state.name);
export const useManualSignalColor = () => useManualSignalStoreSelector((state) => state.color);
export const useManualSignalInterpolationMethod = () =>
  useManualSignalStoreSelector((state) => state.interpolationMethod);
export const useManualSignalMaximumInterpolation = () =>
  useManualSignalStoreSelector((state) => state.maximumInterpolation);
export const useManualSignalUom = () => useManualSignalStoreSelector((state) => state.uom);
export const useManualSignalSamples = () => useManualSignalStoreSelector((state) => state.samples);
export const useManualSignalEditingSampleId = () => useManualSignalStoreSelector((state) => state.editingSampleId);
export const useManualSignalSort = () => useManualSignalStoreSelector((state) => state.sort);
/** The UIConfig to save with the signal on execute. */
export const useManualSignalConfigParams = () => useManualSignalStoreSelector(manualSignalConfigParamsSelector);
/** The formula built from the current samples, or an empty string when there are none. */
export const useManualSignalFormula = () => useManualSignalStoreSelector(manualSignalFormulaSelector);
export const useManualSignalActions = () => useManualSignalStoreInstance().actions;

/** @deprecated Only for the legacy singleton bridge during Flux migration. */
export function legacyCreateManualSignalStore(initialStateOverrides?: Partial<ManualSignalState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    manualSignalActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistManualSignalStore = () => {
  const register = useRegisterPersistedStore();
  const store = useManualSignalStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqManualSignalStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateManualSignalState,
      }),
    [register, store],
  );
};
